import { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { isNil } from 'lodash';
// Import Enums
import { WhatsAppComponentTypeEnum, WhatsAppHeaderFormatEnum } from 'enums';
// Import Components
import { Dropdown } from '@geneui/components';
import SectionWithTitle from 'components/common/SectionWithTitle';
// Import Constants
import { l } from 'constants/common';
// Import SCSS
import styles from './whatsAppPlaceholderMappingSection.module.scss';

const getMappingValue = (mapping, componentKey, paramIndex) => {
    const arr = mapping?.[componentKey];
    return arr?.[paramIndex]?.dynamicVariableFieldName ?? null;
};

const WhatsAppPlaceholderMappingSection = ({
    selectedTemplateByLanguage,
    dynamicVariableOptions,
    value: mapping,
    onChange,
}) => {
    const { t } = useTranslation();

    const placeholderRowsByGroup = useMemo(() => {
        if (isNil(selectedTemplateByLanguage?.Components)) return [];

        return selectedTemplateByLanguage.Components.filter(
            (comp) =>
                comp?.Example?.Params?.length > 0 &&
                !(comp.Type === WhatsAppComponentTypeEnum.Header && comp.Format === WhatsAppHeaderFormatEnum.Image) &&
                comp.Type !== WhatsAppComponentTypeEnum.Buttons,
        ).map((comp) => ({
            componentKey: comp.Type,
            componentType: comp.Type,
            rows: (comp.Example.Params || []).map((param, paramIndex) => ({
                componentKey: comp.Type,
                componentType: comp.Type,
                paramKey: param?.ParamName ?? String(paramIndex + 1),
                paramIndex,
                exampleText: param?.ParamExample ?? '',
            })),
        }));
    }, [selectedTemplateByLanguage]);

    const handleDropdownChange = (componentKey, paramIndex, paramKey, fieldName) => {
        const next = { ...(mapping ?? {}) };
        const arr = [...(next[componentKey] ?? [])];
        while (arr.length <= paramIndex) {
            arr.push({ paramKey: '', dynamicVariableFieldName: null });
        }
        arr[paramIndex] = { paramKey, dynamicVariableFieldName: fieldName };
        next[componentKey] = arr;
        onChange(next);
    };

    if (!placeholderRowsByGroup?.length) {
        return null;
    }

    return (
        <div className={styles.mappingSection}>
            {placeholderRowsByGroup.map(({ componentKey, componentType, rows }) => (
                <SectionWithTitle
                    key={componentKey}
                    title={componentType}
                    hasContentPadding={false}
                    contentClassName={styles.placeholderGroup}
                >
                    {rows.map((row) => (
                        <div key={`${row.componentKey}-${row.paramIndex}`} className={styles.placeholderRow}>
                            <div className={styles.label}>
                                {row.paramKey ? `{${row.paramKey}}` : `${row.paramIndex + 1}`}
                                {row.exampleText ? ` (${row.exampleText})` : null}
                            </div>
                            <Dropdown
                                data={dynamicVariableOptions}
                                value={getMappingValue(mapping, row.componentKey, row.paramIndex)}
                                onChange={(e) =>
                                    handleDropdownChange(
                                        row.componentKey,
                                        row.paramIndex,
                                        row.paramKey,
                                        e?.value ?? null,
                                    )
                                }
                                labelAppearance="swap"
                                isValid={!!getMappingValue(mapping, row.componentKey, row.paramIndex)}
                                errorText={t(l.CannotBeEmpty, { fieldName: l.Placeholder })}
                            />
                        </div>
                    ))}
                </SectionWithTitle>
            ))}
        </div>
    );
};

WhatsAppPlaceholderMappingSection.propTypes = {
    placeholderGroups: PropTypes.arrayOf(
        PropTypes.shape({
            componentKey: PropTypes.string,
            componentType: PropTypes.string,
            rows: PropTypes.arrayOf(
                PropTypes.shape({
                    componentKey: PropTypes.string,
                    componentType: PropTypes.string,
                    paramKey: PropTypes.string,
                    paramIndex: PropTypes.number,
                    exampleText: PropTypes.string,
                }),
            ),
        }),
    ),
    dynamicVariableOptions: PropTypes.arrayOf(
        PropTypes.shape({
            label: PropTypes.string,
            value: PropTypes.string,
        }),
    ),
    value: PropTypes.object,
    onChange: PropTypes.func.isRequired,
};

WhatsAppPlaceholderMappingSection.defaultProps = {
    selectedTemplateByLanguage: [],
    dynamicVariableOptions: [],
    value: null,
};

export default WhatsAppPlaceholderMappingSection;
